import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Tabs, Tab, Button, Card, Modal } from 'react-bootstrap';
import {
  Bell,
  Briefcase,
  ListUl,
  Search,
  BellFill,
  BarChartLine,
  Building,
  Robot,
  Calendar3,
  ArrowClockwise,
  Activity,
  Wallet2,
  CurrencyDollar,
  GraphUpArrow,
} from 'react-bootstrap-icons';
import { playNotificationSound } from '../../shared/utils/tradeNotifications';
import axios from 'axios';
import { toast } from 'react-toastify';
import WatchlistManager from './components/WatchlistManager';
import StockListView from './components/StockListView';
import DependencyNetwork from './components/DependencyNetwork'; // Re-added import
import StockResearchTab from './StockResearchTab'; // Corrected import path
import StockScreener from './components/StockScreener';
import StockAlertRules from './components/StockAlertRules';
import HoldingsManager from './components/HoldingsManager';
import EarningsCalendar from './components/EarningsCalendar';
import AutoTradingRules from './components/AutoTradingRules';
import { formatToLocalTime } from '../../shared/utils/dateUtils';
import './StockMarketDashboard.css';

const TAB_META = {
  holdings: {
    title: 'Holdings',
    description: 'Track open positions, exits, and risk controls in one place.',
    icon: Briefcase,
  },
  watchlists_stocks: {
    title: 'Watchlist',
    description: 'Organize symbols by themes and manage list membership.',
    icon: ListUl,
  },
  screener: {
    title: 'Stock Screener',
    description: 'Filter ideas quickly with valuation and momentum signals.',
    icon: Search,
  },
  alerts: {
    title: 'Alerts',
    description: 'Define trigger rules and monitor high-priority notifications.',
    icon: BellFill,
  },
  analysis: {
    title: 'Analysis',
    description: 'Study dependencies and interconnected risk exposure.',
    icon: BarChartLine,
  },
  stock_research: {
    title: 'Stock Research',
    description: 'Review company fundamentals and technical signals.',
    icon: Building,
  },
  auto_trading: {
    title: 'Auto Trading',
    description: 'Configure automated execution logic and monitor runs.',
    icon: Robot,
  },
  earnings_calendar: {
    title: 'Earnings Calendar',
    description: 'Stay ahead of scheduled events and earnings volatility.',
    icon: Calendar3,
  },
};

const StockMarketDashboard = ({ profileId, token }) => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [key, setKey] = useState('watchlists_stocks');
  const [alerts, setAlerts] = useState([]);
  const [modalAlerts, setModalAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [manualRefreshToken, setManualRefreshToken] = useState(0);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
  
  const authToken = token || localStorage.getItem('token');

  const fetchNotifications = async () => {
    if (!profileId || !authToken) return;
    try {
      const response = await axios.get(
        `${API_BASE}/api/notifications/profiles/${profileId}?unread_only=true&limit=20`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const data = Array.isArray(response.data) ? response.data : [];
      const unreadItems = data.filter(item => !item.read_at);
      const newItems = unreadItems.filter(item => !alerts.find(existing => existing.id === item.id));
      if (newItems.length > 0) {
        console.log('newItems.length: ', newItems.length);
        const severityRank = { LOW: 1, HIGH: 2, CRITICAL: 3 };
        const maxSeverity = newItems.reduce((acc, item) => {
          const next = (item.severity || 'LOW').toUpperCase();
          return severityRank[next] > severityRank[acc] ? next : acc;
        }, 'LOW');
        playNotificationSound(maxSeverity);
        setAlerts(unreadItems);
      }

    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const fetchPortfolioSummary = async () => {
    if (!profileId || !authToken) return;
    try {
      const response = await axios.get(
        `${API_BASE}/api/paper-trading/profiles/${profileId}/portfolio/summary`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (response.data) {
        setPortfolioSummary(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch portfolio summary:', error);
    }
  };

  useEffect(() => {
    setAlerts([]);
    setModalAlerts([]);
    fetchStocksForProfile();
    fetchNotifications();
    fetchPortfolioSummary();

  }, [profileId, authToken]);

  const fetchStocksForProfile = async () => {
    try {
      // Fetch all watchlists for the profile
      const watchlistsResponse = await axios.get(
        `${API_BASE}/api/stock-market/profiles/${profileId}/watchlists`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      
      const watchlists = watchlistsResponse.data || [];
      let allStocks = [];
      
      // Fetch stocks from all watchlists
      for (const watchlist of watchlists) {
        try {
          const stocksResponse = await axios.get(
            `${API_BASE}/api/stock-market/watchlists/${watchlist.id}/stocks`,
            { headers: { Authorization: `Bearer ${authToken}` } }
          );
          const watchlistStocks = stocksResponse.data || [];
          // Add stocks to the combined list (avoid duplicates).
          // Java backend may return either nested { stock, watchlist_item } or flattened stock rows.
          watchlistStocks.forEach(item => {
            const stock = item?.stock || item;
            const symbol = stock?.ticker_symbol;
            if (!symbol) return;
            if (!allStocks.find(s => s.ticker_symbol === symbol)) {
              allStocks.push({
                ...stock,
                latest_price: null, // No price data yet
                watchlist_item_id: item?.watchlist_item?.id || item?.watchlist_item_id || null,
                ticker: symbol // For compatibility
              });
            }
          });
        } catch (error) {
          console.error(`Error fetching stocks for watchlist ${watchlist.id}:`, error);
        }
      }
      
      setStocks(allStocks);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stocks for profile:', error);
      toast.error('Failed to load stocks for watchlist');
      setLoading(false);
    }
  };

  const handleManualRefresh = () => {
    fetchStocksForProfile();
    fetchPortfolioSummary();
    fetchNotifications();
    const now = Date.now();
    localStorage.setItem('paperTradingUpdatedAt', now.toString());
    window.dispatchEvent(new Event('paperTradingUpdated'));
    setManualRefreshToken(now);
  };

  if (loading) {
    return <div className="text-center p-5">Loading Stock Market Dashboard...</div>;
  }

  const handleAlert = (alert) => {
    if (!profileId || !authToken) return;
    const payload = {
      title: alert.title || 'Alert',
      message: alert.message || '',
      details: alert.details || null,
      notification_type: alert.type || null,
      severity: alert.severity || 'LOW',
      source_type: alert.sourceType || null,
      source_id: alert.sourceId || null,
      created_at: alert.timestamp ? new Date(alert.timestamp).toISOString() : null
    };

    axios.post(
      `${API_BASE}/api/notifications/profiles/${profileId}`,
      payload,
      { headers: { Authorization: `Bearer ${authToken}` } }
    ).then((response) => {
      const incoming = response.data;
      if (!incoming) return;
      if (incoming.read_at) return;
      playNotificationSound(incoming.severity || 'LOW');
      setAlerts(prev => {
        const deduped = prev.filter(item => item.id !== incoming.id);
        return [incoming, ...deduped].slice(0, 20);
      });
    }).catch((error) => {
      console.error('Failed to create notification:', error);
    });
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const getProfitLossColor = (value) => {
    if (value === null || value === undefined) return '';
    return value >= 0 ? 'text-success' : 'text-danger';
  };

  const summaryInitialBalance = Number(portfolioSummary?.portfolio?.initial_balance ?? 0);
  const summaryTotalValue = Number(portfolioSummary?.total_value ?? 0);
  const summaryTotalProfitLoss = summaryTotalValue - summaryInitialBalance;
  const summaryTotalProfitLossPercent =
    summaryInitialBalance > 0 ? (summaryTotalProfitLoss / summaryInitialBalance) * 100 : 0;
  const summaryCashBalance = Number(portfolioSummary?.portfolio?.current_balance ?? 0);
  const summaryHoldingsValue = Number(portfolioSummary?.holdings_value ?? 0);

  const dateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
  };

  const tabTitle = (tabKey) => {
    const meta = TAB_META[tabKey];
    const Icon = meta?.icon;
    return (
      <span className="market-tab-title">
        {Icon ? <Icon size={14} /> : null}
        <span>{meta?.title || tabKey}</span>
      </span>
    );
  };

  const renderTabShell = (tabKey, content, options = {}) => {
    const meta = TAB_META[tabKey] || {};
    const showRefresh = options.showRefresh !== false;
    return (
      <Card className="market-tab-shell">
        <Card.Body>
          <div className="market-tab-shell-header">
            <div>
              <div className="market-tab-shell-title">{meta.title || tabKey}</div>
              <div className="market-tab-shell-subtitle">{meta.description || ''}</div>
            </div>
            {showRefresh ? (
              <Button variant="outline-secondary" size="sm" className="market-refresh-btn" onClick={handleManualRefresh}>
                <ArrowClockwise size={13} />
                <span>Refresh</span>
              </Button>
            ) : null}
          </div>
          <div className="market-tab-shell-content">
            {content}
          </div>
        </Card.Body>
      </Card>
    );
  };

  return (
    <Container fluid className="stock-market-dashboard py-4">
      <div className="market-header-panel mb-4">
        <Row className="align-items-center g-3">
          <Col md={7}>
            <div className="market-header-eyebrow">Market Watch Profile</div>
            <h1 className="display-6 mb-1">Market Watch Command Center</h1>
            <p className="text-muted mb-0">Track positions, research symbols, and manage automated trading from one unified workspace.</p>
          </Col>
          <Col md={5} className="d-flex justify-content-md-end align-items-center gap-3">
            <Button
              variant="link"
              className="p-0 text-decoration-none position-relative"
              onClick={() => {
                const unreadIds = alerts.map(alert => alert.id);
                setModalAlerts(alerts);
                setAlerts([]);
                if (profileId && authToken && unreadIds.length > 0) {
                  axios.post(
                    `${API_BASE}/api/notifications/profiles/${profileId}/read`,
                    { ids: unreadIds },
                    { headers: { Authorization: `Bearer ${authToken}` } }
                  ).catch((error) => {
                    console.error('Failed to mark notifications read:', error);
                  });
                }
                setShowAlerts(true);
              }}
              aria-label="Notifications"
            >
              <Bell size={28} />
              {alerts.length > 0 && (
                <span
                  className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                  style={{ fontSize: '0.65rem' }}
                >
                  {alerts.length}
                </span>
              )}
            </Button>
          </Col>
        </Row>
      </div>

      <Row className="mb-4 g-3">
        <Col md={3}>
          <Card className="market-kpi-card h-100">
            <Card.Body>
              <div className="market-kpi-label"><Wallet2 size={14} /> Cash Balance</div>
              <div className="market-kpi-value">{formatCurrency(summaryCashBalance)}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="market-kpi-card h-100">
            <Card.Body>
              <div className="market-kpi-label"><Activity size={14} /> Holdings Value</div>
              <div className="market-kpi-value">{formatCurrency(summaryHoldingsValue)}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="market-kpi-card h-100">
            <Card.Body>
              <div className="market-kpi-label"><CurrencyDollar size={14} /> Total Value</div>
              <div className="market-kpi-value">{formatCurrency(summaryTotalValue)}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className={`market-kpi-card h-100 ${summaryTotalProfitLoss >= 0 ? 'market-kpi-positive' : 'market-kpi-negative'}`}>
            <Card.Body>
              <div className="market-kpi-label"><GraphUpArrow size={14} /> Total P&amp;L</div>
              <div className={`market-kpi-value ${getProfitLossColor(summaryTotalProfitLoss)}`}>
                {formatCurrency(summaryTotalProfitLoss)}
              </div>
              <small className={getProfitLossColor(summaryTotalProfitLossPercent)}>
                {formatPercent(summaryTotalProfitLossPercent)}
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showAlerts} onHide={() => setShowAlerts(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Active Alerts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalAlerts.length > 0 ? (
            <div>
              {modalAlerts.map(alert => {
                const timestamp = alert.created_at || alert.timestamp || Date.now();
                return (
                  <div key={alert.id} className="small text-muted mb-3">
                    <div className="fw-semibold text-dark d-flex align-items-center gap-2">
                      {alert.title}
                      {alert.severity ? (
                        <span className={`badge ${alert.severity === 'CRITICAL' ? 'bg-danger' : alert.severity === 'HIGH' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                          {alert.severity}
                        </span>
                      ) : null}
                    </div>
                    <div>• {alert.message}</div>
                    {alert.details ? (
                      <div className="text-muted">{alert.details}</div>
                    ) : null}
                    <div className="text-muted">
                      {formatToLocalTime(timestamp, dateTimeFormatOptions)}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted mb-0">No active alerts.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowAlerts(false);
              setModalAlerts([]);
            }}
          >
            Mark as Read
          </Button>
        </Modal.Footer>
      </Modal>

      <Tabs activeKey={key} onSelect={(k) => setKey(k)} className="mb-3 market-main-tabs">
        <Tab eventKey="holdings" title={tabTitle('holdings')}>
          {renderTabShell('holdings', (
            <HoldingsManager
              key={`holdings-${manualRefreshToken}`}
              profileId={profileId}
              token={authToken}
              isActive={key === 'holdings'}
              onAlert={handleAlert}
              onRefresh={handleManualRefresh}
            />
          ))}
        </Tab>
        <Tab eventKey="watchlists_stocks" title={tabTitle('watchlists_stocks')}>
          {renderTabShell('watchlists_stocks', (
            <WatchlistManager
              key={`watchlists-${manualRefreshToken}`}
              profileId={profileId}
              token={authToken}
              onStockAdded={fetchStocksForProfile}
              onRefresh={handleManualRefresh}
            />
          ))}
        </Tab>
        <Tab eventKey="screener" title={tabTitle('screener')}>
          {renderTabShell('screener', (
            <StockScreener
              key={`screener-${manualRefreshToken}`}
              profileId={profileId}
              token={authToken}
            />
          ))}
        </Tab>
        <Tab eventKey="alerts" title={tabTitle('alerts')}>
          {renderTabShell('alerts', (
            <StockAlertRules
              key={`alerts-${manualRefreshToken}`}
              profileId={profileId}
              token={authToken}
              isActive={key === 'alerts'}
            />
          ))}
        </Tab>
        <Tab eventKey="analysis" title={tabTitle('analysis')}>
          {renderTabShell('analysis', (
            stocks.length > 0 ? (
              <Tabs defaultActiveKey="dependencies" id="analysis-subtabs" className="mb-3 market-subtabs">
                <Tab eventKey="dependencies" title={tabTitle('analysis')}>
                  <DependencyNetwork
                    key={`dependencies-${manualRefreshToken}`}
                    profileId={profileId}
                    stocks={stocks}
                    token={authToken}
                  />
                </Tab>
              </Tabs>
            ) : (
              <div className="p-4 text-center text-muted">
                <p>Add stocks to your watchlist to access advanced analysis</p>
              </div>
            )
          ))}
        </Tab>
        <Tab eventKey="stock_research" title={tabTitle('stock_research')}>
          {renderTabShell('stock_research', <StockResearchTab key={`research-${manualRefreshToken}`} />)}
        </Tab>
        <Tab eventKey="auto_trading" title={tabTitle('auto_trading')}>
          {renderTabShell('auto_trading', <AutoTradingRules key={`auto-${manualRefreshToken}`} profileId={profileId} token={authToken} />)}
        </Tab>
        <Tab eventKey="earnings_calendar" title={tabTitle('earnings_calendar')}>
          {renderTabShell('earnings_calendar', <EarningsCalendar key={`earnings-${manualRefreshToken}`} token={authToken} />)}
        </Tab>
      </Tabs>
    </Container>
  );
};

export default StockMarketDashboard;
